import { PositionSource } from '@/signalr';
import type { Position } from '@/signalr';
import type { LatLng, MapPoint } from './mapModel';

export type MarkerKind =
  | 'aed'
  | 'amlPositionSource'
  | 'callerLocation'
  | 'caller'
  | 'eedPositionSource'
  | 'home'
  | 'incident'
  | 'location'
  | 'manualPositionSource'
  | 'mobilePositionSource'
  | 'webAppPositionSource';

export type MapMarker = {
  kind: MarkerKind;
  markerId: string;
  latLng: LatLng;
  content: string | null;
  markerIconUrl?: string;
  iconSize?: MapPoint;
  iconAnchor?: MapPoint;
  popupAnchor?: MapPoint;
  positionSource?: PositionSource;
};

const POSITION_SOURCE_ICON_ANCHOR: MapPoint = [15, 22];

export function aedMarker(markerId: string, latLng: LatLng, content: string): MapMarker {
  return { kind: 'aed', markerId, latLng, content, markerIconUrl: 'Images/Icons/map-aed.svg' };
}

export function amlPositionSourceMarker(markerId: string, latLng: LatLng): MapMarker {
  return {
    kind: 'amlPositionSource',
    markerId,
    latLng,
    content: null,
    markerIconUrl: 'Images/Icons/map-aml.svg',
    iconAnchor: POSITION_SOURCE_ICON_ANCHOR,
    positionSource: PositionSource.Aml,
  };
}

export function callerLocationMarker(markerId: string, latLng: LatLng): MapMarker {
  return {
    kind: 'callerLocation',
    markerId,
    latLng,
    content: null,
    markerIconUrl: 'Images/Icons/map-selected.svg',
    iconSize: [49, 70],
    iconAnchor: [24, 60],
    popupAnchor: [0, -62],
  };
}

export function callerMarker(markerId: string, latLng: LatLng): MapMarker {
  return {
    kind: 'caller',
    markerId,
    latLng,
    content: null,
    markerIconUrl: 'Images/Icons/map-caller.svg',
    iconSize: [45, 62],
    iconAnchor: [22, 62],
    popupAnchor: [0, -62],
  };
}

export function eedPositionSourceMarker(markerId: string, latLng: LatLng): MapMarker {
  return {
    kind: 'eedPositionSource',
    markerId,
    latLng,
    content: null,
    markerIconUrl: 'Images/Icons/map-eed.svg',
    iconAnchor: POSITION_SOURCE_ICON_ANCHOR,
    positionSource: PositionSource.Eed,
  };
}

export function homeMarker(markerId: string, latLng: LatLng): MapMarker {
  return { kind: 'home', markerId, latLng, content: null, markerIconUrl: 'Images/Icons/map-home.svg' };
}

export function incidentMarker(markerId: string, latLng: LatLng): MapMarker {
  return { kind: 'incident', markerId, latLng, content: null, markerIconUrl: 'Images/Icons/map-incident.svg' };
}

export function locationMarker(markerId: string, latLng: LatLng, content: string): MapMarker {
  return { kind: 'location', markerId, latLng, content, markerIconUrl: 'Images/Icons/map-icon.svg' };
}

export function manualPositionSourceMarker(markerId: string, latLng: LatLng): MapMarker {
  return {
    kind: 'manualPositionSource',
    markerId,
    latLng,
    content: null,
    markerIconUrl: 'Images/Icons/map-manual.svg',
    iconAnchor: POSITION_SOURCE_ICON_ANCHOR,
    positionSource: PositionSource.Manual,
  };
}

export function mobilePositionSourceMarker(markerId: string, latLng: LatLng): MapMarker {
  return {
    kind: 'mobilePositionSource',
    markerId,
    latLng,
    content: null,
    markerIconUrl: 'Images/Icons/map-device.svg',
    iconAnchor: POSITION_SOURCE_ICON_ANCHOR,
    positionSource: PositionSource.MobileApp,
  };
}

export function webAppPositionSourceMarker(markerId: string, latLng: LatLng): MapMarker {
  return {
    kind: 'webAppPositionSource',
    markerId,
    latLng,
    content: null,
    markerIconUrl: 'Images/Icons/map-device.svg',
    iconAnchor: POSITION_SOURCE_ICON_ANCHOR,
    positionSource: PositionSource.WebApp,
  };
}

export function isPositionSourceMarker(marker: MapMarker): boolean {
  return marker.positionSource !== undefined;
}

export function createMarkerBySource(location: Position): MapMarker {
  const position: LatLng = { lat: location.latitude, lng: location.longitude };
  switch (location.positionSource) {
    case PositionSource.Manual:
      return manualPositionSourceMarker('ManualPositionSourceMarker', position);
    case PositionSource.Aml:
      return amlPositionSourceMarker('AmlPositionSourceMarker', position);
    case PositionSource.Eed:
      return eedPositionSourceMarker('EedPositionSourceMarker', position);
    case PositionSource.WebApp:
      return webAppPositionSourceMarker('WebAppPositionSourceMarker', position);
    case PositionSource.MobileApp:
      return mobilePositionSourceMarker('MobilePositionSourceMarker', position);
    default:
      throw new Error(`${String(location.positionSource)} is unknown PositionSource`);
  }
}
